import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Download, Mail, Sparkles, Check, PlayCircle } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Seo from "@/components/Seo";

export default function WelcomeGift() {
    const [gift, setGift] = useState(null);
    const [firstName, setFirstName] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [subscribed, setSubscribed] = useState(false);

    useEffect(() => {
        http.get("/newsletter/welcome-gift")
            .then((r) => setGift(r.data))
            .catch(() => setGift({ is_active: false }));
    }, []);

    const subscribe = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await http.post("/newsletter/subscribe", {
                email: email.trim().toLowerCase(),
                name: firstName.trim() || null,
                source: "welcome_gift_page",
            });
            setSubscribed(true);
            toast.success("Inscription confirmée — profitez du replay ✨");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (!gift) return <div className="text-center py-24 text-muted-foreground">Chargement…</div>;
    if (!gift.is_active) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="welcome-gift-page">
                <h1 className="font-serif text-4xl mb-4">Cadeau de bienvenue</h1>
                <p className="text-muted-foreground">Aucun cadeau disponible pour le moment. Revenez très bientôt.</p>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-6 py-16" data-testid="welcome-gift-page">
            <Seo
                title={`${gift.title || "Cadeau de bienvenue"} · Foundation of Light`}
                description={gift.description || "Un replay offert à chaque nouveau·elle abonné·e à l'infolettre."}
                path="/cadeau-bienvenue"
            />
            <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium text-center">
                <Gift className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Cadeau de bienvenue
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl text-center mb-4">{gift.title || "Replay offert"}</h1>
            {gift.description && (
                <p className="text-center text-lg text-foreground/70 leading-relaxed max-w-2xl mx-auto mb-10">
                    {gift.description}
                </p>
            )}

            {gift.cover_url && (
                <div className="rounded-3xl overflow-hidden mb-10 shadow-xl border border-border/60">
                    <img src={gift.cover_url} alt={gift.title} className="w-full h-auto object-cover" />
                </div>
            )}

            {subscribed ? (
                <div className="rounded-3xl border-2 border-primary/40 bg-primary/5 p-8 text-center mb-12" data-testid="welcome-gift-unlocked">
                    <div className="mx-auto w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center mb-4">
                        <Check className="w-7 h-7 text-primary" />
                    </div>
                    <h2 className="font-serif text-3xl mb-3">Voici votre replay ✨</h2>
                    <p className="text-sm text-foreground/70 mb-6">
                        Un email de confirmation vient de vous être envoyé avec ces mêmes liens — vous pourrez y revenir quand vous le souhaitez.
                    </p>
                    {gift.video_url && (
                        <video
                            controls
                            preload="metadata"
                            className="w-full rounded-2xl mb-6"
                            poster={gift.cover_url || undefined}
                            data-testid="welcome-gift-video"
                        >
                            <source src={gift.video_url} type="video/mp4" />
                        </video>
                    )}
                    <div className="flex flex-wrap justify-center gap-3">
                        {gift.video_url && (
                            <a href={gift.video_url} download>
                                <Button variant="outline" className="rounded-full h-11 px-6" data-testid="welcome-gift-video-dl">
                                    <Download className="w-4 h-4 mr-2" /> Télécharger la vidéo
                                </Button>
                            </a>
                        )}
                        {gift.audio_url && (
                            <a href={gift.audio_url} download>
                                <Button variant="outline" className="rounded-full h-11 px-6" data-testid="welcome-gift-audio-dl">
                                    <Download className="w-4 h-4 mr-2" /> Télécharger l'audio (m4a)
                                </Button>
                            </a>
                        )}
                    </div>
                </div>
            ) : (
                <form onSubmit={subscribe} className="rounded-3xl border border-primary/30 bg-card p-8 max-w-xl mx-auto mb-12 space-y-4" data-testid="welcome-gift-form">
                    <div className="flex items-start gap-2.5 text-sm text-foreground/80 mb-2">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <span>
                            Inscrivez-vous à l'infolettre — vous accédez immédiatement au replay
                            {gift.duration_minutes ? ` (${gift.duration_minutes} min)` : ""} et vous le recevez aussi par email.
                        </span>
                    </div>
                    <Input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Votre prénom (facultatif)"
                        className="rounded-full h-12"
                        data-testid="welcome-gift-name"
                    />
                    <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="votre@email.com"
                        className="rounded-full h-12"
                        required
                        data-testid="welcome-gift-email"
                    />
                    <Button
                        type="submit"
                        disabled={busy}
                        className="w-full rounded-full h-12 bg-primary text-primary-foreground"
                        data-testid="welcome-gift-submit"
                    >
                        <PlayCircle className="w-4 h-4 mr-2" /> {busy ? "Inscription…" : "Débloquer le replay"}
                    </Button>
                    <p className="text-[11px] text-center text-muted-foreground leading-relaxed">
                        En vous inscrivant, vous acceptez notre <a href="/confidentialite" className="underline">politique de confidentialité</a>. Désinscription en un clic dans chaque email.
                    </p>
                </form>
            )}
        </div>
    );
}
